/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import {
  findContainerElement,
  findFieldElement,
  getFinalContainerElement,
  getScrollableContainer
} from './scroll-to-field-helpers';
import { calculateScrollPosition } from './scroll-to-field-scroll-calc';
import { performSmoothScroll } from './scroll-to-field-scroll-execute';
import { highlightField } from './scroll-to-field-highlight';

/**
 * Performs scroll and highlight for field.
 * 
 * @param {HTMLElement} fieldElement - Field element
 * @param {HTMLElement | null} finalContainerElement - Final container
 * @returns {void}
 */
function performScrollAndHighlight(
  fieldElement: HTMLElement,
  finalContainerElement: HTMLElement | null
): void {
  const scrollableContainer = getScrollableContainer(fieldElement);
  const elementToScroll = finalContainerElement || fieldElement;
  const targetScrollTop = calculateScrollPosition(elementToScroll, scrollableContainer);
  
  performSmoothScroll(scrollableContainer, targetScrollTop);
  highlightField(fieldElement, finalContainerElement);

  // Focus the input to guide the user (after a short delay for smooth scroll)
  const focusable = fieldElement.querySelector('input, select, textarea, [role="combobox"]') as HTMLElement;
  if (focusable) {
    setTimeout(() => {
      focusable.focus();
    }, 500);
  }
}

/**
 * Scrolls to the first missing field in a form.
 * 
 * @param {string | undefined} fieldName - Name of the field to scroll to
 * @returns {void}
 */
export function scrollToFirstMissingField(fieldName: string | undefined): void {
  if (!fieldName) {
    return;
  }
  
  const containerElement = findContainerElement(fieldName);
  const fieldElement = findFieldElement(fieldName, containerElement);
  
  if (!fieldElement) {
    return;
  }
  
  const finalContainerElement = getFinalContainerElement(fieldElement, containerElement);
  performScrollAndHighlight(fieldElement, finalContainerElement);
}
